// Single source of truth for the Digital Cart FAQ.
// Consumed by the on-page accordion (components/DigitalCart.tsx) and the
// FAQPage JSON-LD (app/digital-cart/page.tsx) so the visible question and
// core answer cannot drift from structured data.
// Pricing amount is intentionally not published: the indicative figure is
// pending confirmation. Do not put a rupee amount in these answers.

const DEMO_URL = "https://www.vilvabusiness.com/book-demo?product=digital-cart";
const DEMO_PATH = "/book-demo?product=digital-cart";
const WHATSAPP_URL =
  "https://api.whatsapp.com/send?phone=919176569459";
const CONTACT_LINE = `Contact WhatsApp +91 91765 69459 or ${DEMO_URL}`;

const wa = (label: string) =>
  `<a href="${WHATSAPP_URL}" target="_blank" rel="noopener noreferrer">${label}</a>`;
const demo = (label: string) => `<a href="${DEMO_PATH}">${label}</a>`;

type DigitalCartFaq = {
  question: string;
  /** Core answer. Must match JSON-LD word-for-word (before the contact suffix). */
  answer: string;
  /** Visible follow-up only. Not part of the core answer. */
  nextHtml: string;
};

export const digitalCartFaqs: DigitalCartFaq[] = [
  {
    question: "What is Digital Cart?",
    answer:
      "Digital Cart is a single digital business page from Vilva Business. It holds your profile, products or services, and contact options in one link you can share by URL, QR code, or NFC card — without building a full website.",
    nextHtml: `See a live example or message ${wa("WhatsApp +91 91765 69459")} with “Digital Cart”, or ${demo("book a demo")}.`,
  },
  {
    question: "Who is Digital Cart for?",
    answer:
      "Small business owners, freelancers, service providers, and sales professionals who need a professional online presence that stays updated — a smarter alternative to a static visiting card or a basic link-in-bio page.",
    nextHtml: `${wa("WhatsApp +91 91765 69459")} · ${demo("/book-demo?product=digital-cart")}.`,
  },
  {
    question: "How do people open my Digital Cart?",
    answer:
      "Recipients open it in any mobile or desktop browser — no app install. You share a link, show a QR code, or use an NFC-ready card for a tap-to-open experience.",
    nextHtml: `Ask us on ${wa("WhatsApp")} to set up your page and QR.`,
  },
  {
    question: "What can I show on my Digital Cart?",
    answer:
      "Business profile and hours, location and contact details, product or service listings with images and prices, click-to-call, WhatsApp, QR sharing, NFC support, and a lead/enquiry form.",
    nextHtml: `Send your logo and offer list on ${wa("WhatsApp +91 91765 69459")}.`,
  },
  {
    question: "Is Digital Cart the same as Vilva D2C or an online store?",
    answer:
      "No. Digital Cart is a lightweight digital business card / catalogue page. Vilva D2C is a full ecommerce platform (cart, checkout, inventory, logistics). Choose Digital Cart for presence and enquiries; choose D2C when you need a complete store.",
    nextHtml: `Tell us your goal on ${wa("WhatsApp")} — we will recommend the right product.`,
  },
  {
    question: "Does Digital Cart work on mobile and desktop?",
    answer:
      "Yes. Pages are built to open smoothly on phone, tablet, and desktop browsers without requiring an app.",
    nextHtml: `Book a quick walkthrough at ${demo("/book-demo?product=digital-cart")}.`,
  },
  {
    question: "Can I collect leads with Digital Cart?",
    answer:
      "Yes. The lead collection form captures enquiries and contact details so you can follow up by call or WhatsApp.",
    nextHtml: `${wa("WhatsApp +91 91765 69459")} to enable lead capture on your page.`,
  },
  {
    question: "How is Digital Cart priced?",
    answer:
      "Digital Cart is positioned as simple, one-time pricing including web/mobile access, profile setup, listings, QR, NFC-ready support, contact/lead form, and basic support. The published indicative figure is pending confirmation. Taxes extra if applicable.",
    nextHtml: `Confirm current price on ${wa("WhatsApp +91 91765 69459")} or ${demo("book a demo")}.`,
  },
  {
    question: "How is my data handled?",
    answer:
      "We apply standard web security practices appropriate for a business profile and lead form (HTTPS, access controls on the admin side). Digital Cart is not a full ecommerce payment vault — payment collection, if needed, should use your existing payment tools or a Vilva commerce product.",
    nextHtml: `Ask security questions on ${wa("WhatsApp")} before you publish personal data on the page.`,
  },
  {
    question: "How do I get started?",
    answer:
      "Share your business name, logo, services, and contact number. Vilva sets up your Digital Cart, QR, and sharing options.",
    nextHtml: `Message <strong>Digital Cart setup</strong> to ${wa("WhatsApp +91 91765 69459")} or open ${demo("/book-demo?product=digital-cart")}.`,
  },
];

/** Accordion items: core answer text, then the pack's next-step line. */
export const digitalCartFaqItems = digitalCartFaqs.map((faq) => ({
  question: faq.question,
  answer: `${faq.answer}<br/><br/>${faq.nextHtml}`,
}));

export const faqSchema = {
  "@type": "FAQPage",
  "@id": "https://www.vilvabusiness.com/digital-cart#faq",
  mainEntity: digitalCartFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: `${faq.answer} ${CONTACT_LINE}`,
    },
  })),
};
